import { effect, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { USER_ACTIVITY_EVENTS } from 'src/app/app.constants';
import { toHHMMSS } from 'src/app/app.utils';
import { AuthService } from './auth.service';
import { InactivityService } from './inactivity.service';

/**
 * Monitors user activity in HMI mode and automatically logs out an authenticated user after
 * inactivity. When no user is logged in the service is idle — the well connection is never
 * affected.
 *
 * Flow:
 * 1. Any DOM activity resets the idle timer, but only while a user is logged in.
 * 2. Login starts the timer; logout (any cause) stops it immediately.
 * 3. After the idle period a warning toast appears with a countdown.
 * 4. Tapping the toast resets the timer.
 * 5. If the toast auto-closes, authService.logout() is called.
 */
@Injectable()
export class InactivityHmiService extends InactivityService implements OnDestroy {
  // Inactivity period before showing the warning toast.
  private readonly _INACTIVITY_MS = 15 * 60 * 1000; // 15 minutes
  // Time from showing the warning toast to auto-logout.
  private readonly _WARNING_MS = 1 * 60 * 1000; // 1 minute

  /** Unused in HMI mode — the reconnect overlay is never shown, see isDisconnected below. */
  override readonly inactivityMinutes = this._INACTIVITY_MS / (60 * 1000);

  /** Always false in HMI mode — logout replaces the reconnect overlay. */
  public override readonly isDisconnected = signal(false);

  private _timer: ReturnType<typeof setTimeout> | null = null;
  private _activeToast: ActiveToast<unknown> | null = null;
  private _toastTapSub: Subscription | null = null;
  private _countdownInterval: ReturnType<typeof setInterval> | null = null;

  private readonly _boundReset = this._resetFromActivity.bind(this);
  private readonly _activityEvents = USER_ACTIVITY_EVENTS;
  private toastr = inject(ToastrService);
  private authService = inject(AuthService);
  constructor() {
    super();

    this._activityEvents.forEach((ev) =>
      document.addEventListener(ev, this._boundReset, { passive: true, capture: true }),
    );

    // Start the timer when a user logs in; stop everything when they log out.
    effect(() => {
      if (this.authService.isAuthenticated()) {
        this._reset();
      } else {
        this._clearAll();
      }
    });
  }

  /** Handles user activity events and resets inactivity timers for authenticated users only. */
  private _resetFromActivity(): void {
    if (!this.authService.isAuthenticated()) return;
    this._reset();
  }

  /** Resets the inactivity timer and clears any active warning/countdown state. */
  private _reset(): void {
    this._clearCountdown();
    this._clearActiveToast();
    if (this._timer !== null) clearTimeout(this._timer);
    this._timer = setTimeout(() => this._warn(), this._INACTIVITY_MS);
  }

  /** Shows a warning toast and starts a per-second logout countdown. */
  private _warn(): void {
    let remaining = this._WARNING_MS;

    this._activeToast = this.toastr.warning(
      'Tap to stay logged in.',
      `You will be logged out after ${toHHMMSS(remaining)} due to ` +
        `${this.inactivityMinutes} minutes of user inactivity`,
      { disableTimeOut: true, tapToDismiss: true },
    );

    this._countdownInterval = setInterval(() => {
      remaining -= 1000;
      const titleEl = (
        this._activeToast?.portal?.location?.nativeElement as HTMLElement
      )?.querySelector('.toast-title');
      if (titleEl) {
        titleEl.textContent =
          `You will be logged out after ${toHHMMSS(remaining)} due to ` +
          `${this.inactivityMinutes} minutes of user inactivity`;
      }

      if (remaining <= 0) {
        this._clearCountdown();
        const toastId = this._activeToast?.toastId;
        this._clearActiveToast();
        if (toastId !== undefined) this.toastr.clear(toastId);
        this.authService.logout();
      }
    }, 1000);

    this._toastTapSub?.unsubscribe();
    this._toastTapSub = this._activeToast.onTap.pipe(take(1)).subscribe(() => this._reset());
  }

  /** Clears all timers, countdowns, and toasts. */
  private _clearAll(): void {
    this._clearCountdown();
    this._clearActiveToast();
    if (this._timer !== null) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  /** Stops the warning countdown interval if one is running. */
  private _clearCountdown(): void {
    if (this._countdownInterval !== null) {
      clearInterval(this._countdownInterval);
      this._countdownInterval = null;
    }
  }

  /** Dismisses the active toast and unsubscribes from toast interaction handlers. */
  private _clearActiveToast(): void {
    if (this._activeToast !== null) {
      this.toastr.clear(this._activeToast.toastId);
      this._activeToast = null;
    }

    this._toastTapSub?.unsubscribe();
    this._toastTapSub = null;
  }

  /** Removes document listeners and fully clears service state on destroy. */
  public override ngOnDestroy(): void {
    this._activityEvents.forEach((ev) =>
      document.removeEventListener(ev, this._boundReset, { capture: true }),
    );
    this._clearAll();
  }
}
